// =============================================================================
// live-cards.js – HTML rendering for LIVE tab cards
// =============================================================================
// Produces HTML strings for the various card types (featured, line, train,
// recently departed). These functions take all data as explicit parameters
// (no module-level state), so they are deterministic given their inputs.
// =============================================================================

import {
  minsToHHMM,
  getDayType,
  getUrgencyState,
  getUrgencyClass,
  getReachabilityState,
  getRecentlyDeparted,
  formatWait,
  getScheduleKey,
  escapeHtml
} from "./utils.js";
import { classifyConnection, getThresholdsFromState } from "./connection-classifier";
import {
  buildHeroViewModel,
  buildTimeInfoViewModel,
  buildTrainCardViewModel
} from "./hero-viewmodel";
import { buildJourneyChain } from "./journey-chain";
import { renderProgressBar } from "./connection-progress";
import { calcNextTrain, buildCanegrateBlock, buildTrainStationBlock } from "./trains";
import { getHeroConnectionInfo } from "./now-banner.js";
import { TRAIN_STATIONS } from "../data/trains.js";
import { getStopName, RETURN_DESTINATIONS } from "./line-config.js";
import { STOP_COORDINATES } from "./map-data.js";
import { getProfile, getUserFavorite } from "./live-logic";
import { getWalkMinForStop } from "./walk-per-stop";
import { getAlertsForLine } from "./alerts.js";
import { renderJourneyChain } from "./journey-chain-component.js";

export function hasAccessibilityFlag(trip) {
  if (!trip || !Array.isArray(trip.flags)) return false;
  return trip.flags.some(f => {
    const lower = String(f).toLowerCase();
    return lower.includes("accessible") || lower.includes("low-floor") || lower.includes("wheelchair") || lower.includes("pedana");
  });
}

// ─── Inline SVG icons ───────────────────────────────────────────────────────

export const PIN_SVG = `<svg class="pin-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>`;
export const EDIT_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="edit-icon-svg"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>`;
export const CALENDAR_SVG = `<svg class="btn-icon calendar-icon-svg" viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>`;

// ─── Featured (Hero) bus card ───────────────────────────────────────────────

export function renderFeaturedCard(card, direction, cfg, currentMin, state) {
  const vm = buildHeroViewModel(card, direction, cfg, currentMin, state);
  const primaryStops = renderStopChips(vm.trip, vm.compactStops, "large", state, cfg);
  const hasMapPin = vm.hasMapPin;
  const pinHtml = hasMapPin
    ? `<button type="button" class="map-trigger dep-stop-pin" data-stop-code="${vm.fromStop}" title="Vedi sulla mappa" aria-label="Apri mappa fermata">${PIN_SVG}</button>`
    : "";

  let selectHtml = "";
  if (vm.stopCandidates && vm.stopCandidates.length > 1) {
    const selectValue = vm.preferredStop || vm.fromStop;
    const options = vm.stopCandidates
      .map((code) => {
        const isSelected = code === selectValue;
        return `<option value="${code}" ${isSelected ? "selected" : ""}>${escapeHtml(getStopName(code))}</option>`;
      })
      .join("");
    selectHtml = `
      <span class="dep-stop-edit-wrapper">
        <span class="dep-stop-name-text">${escapeHtml(vm.fromStopName)}</span>
        <button type="button" class="edit-dep-stop-btn" title="Modifica fermata di partenza" aria-label="Modifica fermata di partenza">
          ${EDIT_SVG}
        </button>
        <select class="dep-stop-select hidden-select" data-line="${vm.lineId}" data-dir="${vm.direction}">
          ${options}
        </select>
      </span>
    `;
  } else {
    selectHtml = `<span class="dep-stop-name-text">${escapeHtml(vm.fromStopName)}</span>`;
  }

  const depStopHtml = `
    <div class="dep-stop-row dep-stop-row--hero">
      <div class="dep-stop-row-inner">
        <div class="dep-stop-group">
          <span class="dep-stop-label">PARTENZA DA</span>
          <div class="dep-stop-controls">
            ${selectHtml}
            ${pinHtml}
            <span class="dep-stop-dest">→ ${escapeHtml(vm.destination)}</span>
          </div>
        </div>
        <button type="button" class="btn text-btn view-timetable-btn" data-timetable-line="${vm.lineId}" data-timetable-dir="${vm.direction}">
          ${CALENDAR_SVG} Orari linea
        </button>
      </div>
      <div class="pref-confirm-label">
        ✓ Impostata come preferita per ${vm.lineId}!
      </div>
    </div>
  `;

  const tripDepMin = vm.trip?._depMin ?? 0;
  const leaveAtMs = getLeaveAtMs(tripDepMin, vm.walkMin, currentMin);

  const walkBlock =
    vm.direction === "outbound"
      ? `
      <div class="time-block walk">
        <div class="time-block-label walk">Uscire di casa</div>
        <h2 class="time-block-value-hero" data-leave-ts="${leaveAtMs}" aria-live="polite" aria-label="Tempo stimato alla partenza a piedi: tra ${vm.leaveCountdown} minuti">tra ${vm.leaveCountdown} min</h2>
        <span class="time-block-pill walk">a piedi</span>
        <div class="time-block-detail">Uscire alle ${vm.leaveAtHHMM} &middot; ${vm.walkMin} min a piedi</div>
      </div>
  `
      : "";

  const chain = buildJourneyChain(card, direction, cfg, state, currentMin);
  const thresholds = getThresholdsFromState(state, cfg);
  const journeyChainHtml = renderJourneyChain(chain, thresholds);

  const conn = getHeroConnectionInfo(card, cfg, state);
  let connBadgeHtml = "";
  if (conn) {
    const displayTime = conn.type === "metro" ? "" : ` +${conn.waitMin}′`;
    connBadgeHtml = `
      <div class="hero-conn-badge-row">
        <span class="conn-hero-badge conn-state-${conn.state.css}">
          coinc. <strong>${escapeHtml(conn.line)}</strong> a ${escapeHtml(conn.station)}${displayTime} &bull; ${escapeHtml(conn.state.label)}
        </span>
      </div>
    `;
  }

  return `<section class="featured-card ${vm.urgency.css}">
    <div class="featured-top">
      <span class="line-pill">${vm.lineId}</span>
      <span class="status-pill ${vm.urgency.css}">${vm.urgency.label}</span>
      ${hasAccessibilityFlag(vm.trip) ? `<span class="accessibility-pill" title="Veicolo con pianale ribassato / pedana disabili" style="background: var(--surface-3); border: 1px solid var(--line); border-radius: 6px; padding: 2px 6px; font-size: 0.8rem; display: inline-flex; align-items: center; justify-content: center; height: fit-content; margin-left: 4px;">♿</span>` : ""}
    </div>
    ${depStopHtml}
    ${connBadgeHtml}
    <div class="time-info-row time-info-row--no-border">
      <div class="time-block bus">
        <div class="time-block-label bus">Orario Bus</div>
        <h2 class="time-block-value-hero-time">${vm.depHHMM}</h2>
        <span class="time-block-pill bus" aria-live="polite" aria-label="Partenza del bus tra ${vm.wait} minuti">tra ${vm.wait} min</span>
      </div>
      ${walkBlock}
    </div>
    ${primaryStops}
    ${journeyChainHtml}
    ${vm.direction === "outbound" && vm.lineId === "Z649" ? renderZ649DestinationEstimates(vm.trip, cfg) : ""}
  </section>`;
}

// Local renderJourneyChain removed in favor of imported vertical timeline component

// ─── Standard bus line card ─────────────────────────────────────────────────

export function renderLineCard(card, direction, cfg, currentMin, state) {
  if (!card) return "";
  const id = `live-${card.lineId}`;
  const isExpanded = false;
  const trip = card.trip;
  const walkMin = card.fromStop ? getWalkMinForStop(card.fromStop, state, cfg) : 0;
  const wait = trip && typeof trip._depMin === "number" ? trip._depMin - currentMin : null;
  const urgencyClass = trip && wait !== null ? getUrgencyClass(wait) : "urgency-missed";
  const compact = trip && card.compactStops ? renderStopChips(trip, card.compactStops, "", state, cfg) : "";
  const returnDest =
    card.config?.returnDestination ||
    (card.lineId ? RETURN_DESTINATIONS[card.lineId] : "") ||
    "Busto Garolfo";
  const toDestName = card.toStop ? getStopName(card.toStop) : returnDest;
  const fromName = card.fromStop ? getStopName(card.fromStop) : "Fermata sconosciuta";
  const routeLabel =
    direction === "return"
      ? `${fromName} -> ${toDestName}`
      : `${fromName} -> ${card.config?.destination || ""}`;
  const cardDest = direction === "return" ? toDestName : card.config?.destination || "";
  const fallback = trip?._usedFallback
    ? `<span class="notice-inline">fermata alternativa</span>`
    : "";
  const nextDisplay =
    trip && wait !== null ? `${minsToHHMM(trip._depMin)} · ${formatWait(wait)}` : "Nessun bus";
  const timeInfoVm = buildTimeInfoViewModel(
    trip,
    wait,
    walkMin,
    direction,
    card.fromStop || "",
    cardDest,
    card
  );

  const lineAlerts = getAlertsForLine(card.lineId);
  const hasAlerts = lineAlerts.length > 0;
  const alertBadge = hasAlerts
    ? `<span class="alert-badge" title="${lineAlerts.length} avvisi attivi" data-switch-to-tab="alerts" style="background: var(--danger); color: white; padding: 2px 6px; border-radius: 4px; font-size: 0.72rem; font-weight: 700; display: inline-flex; align-items: center; gap: 4px; cursor: pointer; margin-left: 8px;">⚠️ ${lineAlerts.length}</span>`
    : "";

  let alertsDetailsHtml = "";
  if (hasAlerts) {
    const alertsListHtml = lineAlerts
      .map((a) => {
        const sourceLabel = { trenord: "Trenord", atm: "ATM Milano" }[a.source] || a.source;
        return `<div class="card-expanded-alert-item" style="border-left: 3px solid var(--danger); padding-left: 8px; margin-bottom: 8px; font-size: 0.78rem;">
        <strong style="color: var(--danger);">${escapeHtml(sourceLabel)}:</strong> ${escapeHtml(a.title)}
      </div>`;
      })
      .join("");

    alertsDetailsHtml = `<div class="card-expanded-alerts" style="margin-bottom: 12px; padding: 10px; background: rgba(239, 68, 68, 0.05); border: 1px solid rgba(239, 68, 68, 0.15); border-radius: 6px;">
      <h4 style="margin: 0 0 6px 0; font-size: 0.82rem; color: var(--danger); font-weight: 700; display: flex; align-items: center; gap: 6px;">⚠️ Disservizi / Scioperi attivi</h4>
      ${alertsListHtml}
    </div>`;
  }

  return `<article class="line-card ${isExpanded ? "expanded" : ""}" data-card="${id}">
    <button class="line-card-header" type="button" data-toggle-card="${id}">
      <div class="line-main">
        <span class="line-pill">${card.lineId}</span>
        ${alertBadge}
        ${hasAccessibilityFlag(trip) ? `<span class="accessibility-pill" title="Veicolo con pianale ribassato / pedana disabili" style="background: var(--surface-3); border: 1px solid var(--line); border-radius: 6px; padding: 2px 6px; font-size: 0.8rem; display: inline-flex; align-items: center; justify-content: center; height: fit-content; margin-left: 4px;">♿</span>` : ""}
        <div>
          <h3>${escapeHtml(routeLabel)}</h3>
          <p>${fallback}${renderValidity(card.validities)}</p>
        </div>
      </div>
      <div class="line-meta">
        <strong aria-live="polite" aria-label="Prossima partenza: ${nextDisplay}">${nextDisplay}</strong>
        <span class="urgency-dot ${urgencyClass}"></span>
    </button>
    ${renderTimeInfoRow(timeInfoVm, currentMin, trip)}
    ${compact}
    <div class="line-card-body">
      ${alertsDetailsHtml}
      ${card.disrupted ? renderDisruption(card.lineId, cfg) : ""}
      ${trip ? renderTripDetails(card, cfg, currentMin, state) : `<div class="empty-mini">Nessuna corsa nelle prossime ore.</div>`}
      ${renderUpcomingTrips(card)}
    </div>
  </article>`;
}

export function renderTimeInfoRow(vm, currentMin, trip) {
  if (!vm) return "";
  const pinHtml = vm.hasMapPin
    ? `<button type="button" class="map-trigger dep-stop-pin" data-stop-code="${vm.fromStop}" title="Vedi sulla mappa" aria-label="Apri mappa fermata">${PIN_SVG}</button>`
    : "";

  let selectHtml = "";
  if (vm.stopCandidates && vm.stopCandidates.length > 1) {
    const selectValue = vm.preferredStop || vm.fromStop;
    const options = vm.stopCandidates
      .map((code) => {
        const isSelected = code === selectValue;
        return `<option value="${code}" ${isSelected ? "selected" : ""}>${escapeHtml(getStopName(code))}</option>`;
      })
      .join("");
    selectHtml = `
      <span class="dep-stop-edit-wrapper">
        <span class="dep-stop-name-text">${escapeHtml(vm.fromStopName)}</span>
        <button type="button" class="edit-dep-stop-btn" title="Modifica fermata di partenza" aria-label="Modifica fermata di partenza">
          ${EDIT_SVG}
        </button>
        <select class="dep-stop-select hidden-select" data-line="${vm.lineId}" data-dir="${vm.direction}">
          ${options}
        </select>
      </span>
    `;
  } else {
    selectHtml = `<span class="dep-stop-name-text">${escapeHtml(vm.fromStopName)}</span>`;
  }

  const depStopHtml = `
    <div class="dep-stop-row dep-stop-row--standard">
      <div class="dep-stop-row-inner">
        <div class="dep-stop-group">
          <span class="dep-stop-label">PARTENZA DA</span>
          <div class="dep-stop-controls">
            ${selectHtml}
            ${pinHtml}
            <span class="dep-stop-dest">→ ${escapeHtml(vm.destination)}</span>
          </div>
        </div>
        ${
          vm.lineId
            ? `
        <button type="button" class="btn text-btn view-timetable-btn" data-timetable-line="${vm.lineId}" data-timetable-dir="${vm.direction}">
          ${CALENDAR_SVG} Orari linea
        </button>
        `
            : ""
        }
      </div>
      ${
        vm.lineId
          ? `
      <div class="pref-confirm-label">
        ✓ Impostata come preferita per ${vm.lineId}!
      </div>
      `
          : ""
      }
    </div>
  `;

  const tripDepMin = trip?._depMin ?? 0;
  const leaveAtMs = getLeaveAtMs(tripDepMin, vm.walkMin, currentMin);

  return `
    ${depStopHtml}
    <div class="time-info-row">
      <div class="time-block bus">
        <div class="time-block-label bus">Orario Bus</div>
        <div class="time-block-value bus">${vm.depHHMM}</div>
        <span class="time-block-pill bus" aria-live="polite" aria-label="Partenza del bus tra ${vm.wait} minuti">tra ${vm.wait} min</span>
      </div>
      ${
        vm.direction === "outbound"
          ? `
      <div class="time-block walk">
        <div class="time-block-label walk">Uscire di casa</div>
        <div class="time-block-value walk" data-leave-ts="${leaveAtMs}" aria-live="polite" aria-label="Partenza a piedi tra ${vm.leaveCountdown} minuti">tra ${vm.leaveCountdown} min</div>
        <span class="time-block-pill walk">a piedi</span>
        <div class="time-block-detail">Uscire alle ${vm.leaveAtHHMM} &middot; ${vm.walkMin} min a piedi</div>
      </div>`
          : ""
      }
    </div>
  `;
}

export function renderValidity(validities) {
  if (!validities.length) return "";
  return validities.map((v) => `<span class="validity-badge">${escapeHtml(v)}</span>`).join("");
}

export function renderDisruption(lineId, cfg) {
  const d = cfg.serviceDisruptions?.[lineId]?.[0];
  return `<div class="banner banner-warning">Possibile sospensione fino al ${escapeHtml(d?.to || "?")}: ${escapeHtml(d?.note || "")}</div>`;
}

export function renderStopChips(trip, stops, size = "", state = null, cfg = null) {
  const chips = (stops || [])
    .map((code) => {
      const mins = trip.stops?.[code];
      const hasPin = STOP_COORDINATES?.[code];
      const stopEl = hasPin
        ? `<small class="map-trigger" data-stop-code="${code}" title="${escapeHtml(getStopName(code))} [${code}] - Vedi sulla mappa">${escapeHtml(getStopName(code))} ${PIN_SVG}</small>`
        : `<small title="${escapeHtml(getStopName(code))} [${code}]">${escapeHtml(getStopName(code))}</small>`;

      // V2 - Chip fermate ricche
      let walkBadge = "";
      if (state && cfg) {
        const walkMin = getWalkMinForStop(code, state, cfg);
        if (walkMin > 0) {
          walkBadge = `<span class="stop-chip-walk" title="Distanza a piedi: ${walkMin} min">🚶 ${walkMin}m</span>`;
        }
      }

      let interchangeHtml = "";
      if (cfg) {
        const interchange = cfg.interchanges?.[code];
        if (interchange) {
          const icon = interchange.type === "M1" ? "Ⓜ️" : "🚆";
          interchangeHtml = `<span class="stop-chip-interchange" title="Stazione di interscambio: ${escapeHtml(interchange.label)}">${icon}</span>`;
        }
      }

      const badgesHtml = walkBadge || interchangeHtml
        ? `<div class="stop-chip-badges">${walkBadge}${interchangeHtml}</div>`
        : "";

      return `<span class="stop-chip ${size}">
        ${stopEl}
        ${badgesHtml}
        <strong>${mins !== undefined && mins !== null ? minsToHHMM(mins) : "-"}</strong>
      </span>`;
    })
    .join("");
  return chips ? `<div class="stop-chip-row">${chips}</div>` : "";
}

export function renderTripDetails(card, cfg, currentMin, state) {
  const trip = card.trip;
  let foundUpcoming = false;
  const timeline = card.detailStops
    .map((code) => ({ code, minutes: trip.stops?.[code] }))
    .filter((s) => s.minutes !== undefined && s.minutes !== null)
    .map((s) => {
      const isPast = s.minutes < currentMin;
      let statusClass = "";
      if (isPast) {
        statusClass = "past";
      } else if (!foundUpcoming) {
        statusClass = "upcoming";
        foundUpcoming = true;
      }

      const hasPin = STOP_COORDINATES?.[s.code];
      const stopEl = hasPin
        ? `<small class="map-trigger" data-stop-code="${s.code}" title="${escapeHtml(getStopName(s.code))} [${s.code}] - Vedi sulla mappa">${escapeHtml(getStopName(s.code))} ${PIN_SVG}</small>`
        : `<small title="${escapeHtml(getStopName(s.code))} [${s.code}]">${escapeHtml(getStopName(s.code))}</small>`;
      return `<div class="timeline-node ${statusClass}">
        <span></span>
        <strong>${minsToHHMM(s.minutes)}</strong>
        ${stopEl}
      </div>`;
    })
    .join("");

  const connections = renderConnections(card, cfg, currentMin, state);
  return `<div class="timeline">${timeline}</div>${connections}`;
}

export function renderStationLink(slotKey, cfg) {
  const link = cfg.stationLinks?.[slotKey];
  if (!link) return "";
  return ` <a href="${escapeHtml(link.url)}" target="_blank" rel="noopener" class="station-link" title="Orari tempo reale ${escapeHtml(link.label)}">🔴 LIVE</a>`;
}

export function renderConnections(card, cfg, currentMin, state) {
  const trip = card.trip;
  const thresholds = getThresholdsFromState(state, cfg);

  // ── RETURN: show how to reach the bus from various origins ──
  if (card.direction === "return") {
    const origins = card.returnOrigins
      .map((origin) => {
        const busAtInterchange = trip.stops?.[origin.interchangeStop];
        if (busAtInterchange === undefined || busAtInterchange === null) return "";
        const leaveOrigin = busAtInterchange - origin.minutesToInterchange;
        // Connection quality based on how much time until user must leave
        const marginMin = leaveOrigin - currentMin;
        const connState = classifyConnection(marginMin, thresholds);
        return `<div class="connection-row">
          <span>${escapeHtml(origin.label)} → ${escapeHtml(getStopName(origin.interchangeStop))}</span>
          <strong>parti ${minsToHHMM(leaveOrigin)} → bus ${minsToHHMM(busAtInterchange)}</strong>
          <em class="${connState.css}">${connState.label}</em>
        </div>`;
      })
      .filter(Boolean)
      .join("");

    // Add station links for return interchanges
    let stationLinks = "";
    const seenLinks = new Set();
    for (const origin of card.returnOrigins || []) {
      const interchange = cfg.interchanges?.[origin.interchangeStop];
      if (interchange?.trainSlot && !seenLinks.has(interchange.trainSlot)) {
        seenLinks.add(interchange.trainSlot);
        const linkHtml = renderStationLink(interchange.trainSlot, cfg);
        if (linkHtml) stationLinks += linkHtml;
      }
    }

    return origins
      ? `<div class="connection-box"><h4>Coincidenze stimate${stationLinks}</h4>${origins}</div>`
      : "";
  }

  // ── OUTBOUND: show train/metro connections at destination stops ──
  const rows = [];
  const seen = new Set();
  let isFirstTrain = true;
  // Check BOTH detail and compact stops for connections (deduplicated)
  const connectionStops = [...new Set([...(card.detailStops || []), ...(card.compactStops || [])])];

  for (const code of connectionStops) {
    const arrMin = trip.stops?.[code];
    const interchange = cfg.interchanges?.[code];
    if (arrMin === undefined || arrMin === null || !interchange) continue;
    if (seen.has(interchange.label)) continue;
    seen.add(interchange.label);

    if (interchange.trainSlot) {
      // Primary train connection (S5)
      const train = calcNextTrain(interchange.trainSlot, arrMin)[0];
      if (train) {
        const stateObj = classifyConnection(train.waitMin, thresholds);
        let detailHtml = `<em class="${stateObj.css}">${stateObj.label}</em>`;
        if (isFirstTrain) {
          detailHtml = renderProgressBar(train.waitMin, thresholds);
          isFirstTrain = false;
        }
        rows.push(`<div class="connection-row">
          <span>${escapeHtml(interchange.label)} · ${escapeHtml(train.line)}${train.note ? ` <small>${escapeHtml(train.note)}</small>` : ""}</span>
          <strong>${minsToHHMM(train.departureMin)} (+${train.waitMin} min)</strong>
          ${detailHtml}
        </div>`);
      }
      // Secondary train connection (RE) if available
      if (interchange.trainSlotRE) {
        const trainRE = calcNextTrain(interchange.trainSlotRE, arrMin)[0];
        if (trainRE) {
          const stateRE = classifyConnection(trainRE.waitMin, thresholds);
          let detailHtmlRE = `<em class="${stateRE.css}">${stateRE.label}</em>`;
          if (isFirstTrain) {
            detailHtmlRE = renderProgressBar(trainRE.waitMin, thresholds);
            isFirstTrain = false;
          }
          rows.push(`<div class="connection-row">
            <span>${escapeHtml(interchange.label)} · ${escapeHtml(trainRE.line)}${trainRE.note ? ` <small>${escapeHtml(trainRE.note)}</small>` : ""}</span>
            <strong>${minsToHHMM(trainRE.departureMin)} (+${trainRE.waitMin} min)</strong>
            ${detailHtmlRE}
          </div>`);
        }
      }
    } else if (interchange.type === "M1") {
      rows.push(`<div class="connection-row">
        <span>${escapeHtml(interchange.label)}</span>
        <strong>M1</strong>
      </div>`);
    }
  }

  if (!rows.length) return "";

  // Collect station links for outbound connections
  let stationLinks = "";
  const seenLinks = new Set();
  for (const code of connectionStops) {
    const interchange = cfg.interchanges?.[code];
    if (!interchange) continue;
    if (interchange.trainSlot && !seenLinks.has(interchange.trainSlot)) {
      seenLinks.add(interchange.trainSlot);
      stationLinks += renderStationLink(interchange.trainSlot, cfg);
    }
  }

  return `<div class="connection-box"><h4>Coincidenze stimate${stationLinks}</h4>${rows.join("")}</div>`;
}

export function renderZ649DestinationEstimates(trip, cfg) {
  const pg = trip.stops?.PG102;
  const md = trip.stops?.MD111;
  const parts = [];
  if (pg !== undefined) {
    const rep = cfg.s5s6_destinations.find((d) => d.name === "Repubblica");
    if (rep) {
      const nextTrains = calcNextTrain("PG102", pg, 1);
      if (nextTrains.length > 0) {
        const train = nextTrains[0];
        parts.push(
          `Repubblica via Pregnana ~${minsToHHMM(train.departureMin + rep.minutesFromPregnana)}`
        );
      } else {
        parts.push(`Repubblica via Pregnana ~${minsToHHMM(pg + rep.minutesFromPregnana)}`);
      }
    }
  }
  if (md !== undefined) {
    const repM1 = cfg.m1_destinations.find((d) => d.id === "repubblica");
    if (repM1) parts.push(`Repubblica via M1 ~${minsToHHMM(md + repM1.minutesFromMolino)}`);
  }
  if (!parts.length) parts.push("Repubblica: questa corsa non mostra una coincidenza utile");
  return `<div class="destination-estimates">${parts.map(escapeHtml).join(" · ")}</div>`;
}

export function renderUpcomingTrips(card) {
  if (card.nextTrips.length <= 1) return "";
  return `<div class="next-list">
    <h4>Prossime corse</h4>
    ${card.nextTrips
      .slice(1)
      .map(
        (t) => `<div>
      <span>${minsToHHMM(t._depMin)} da ${escapeHtml(getStopName(t._depStop))}</span>
      <strong>${escapeHtml(t.validity || "")}</strong>
    </div>`
      )
      .join("")}
  </div>`;
}

// ─── Recently departed block ────────────────────────────────────────────────

export function renderRecentlyDepartedBlock(
  state,
  lineData,
  lineConfig,
  cfg,
  currentMin,
  dayType,
  direction
) {
  const recent = [];
  for (const lineId of cfg.lineOrder || []) {
    const profile = getProfile(cfg, lineId);
    const key = getScheduleKey(lineId, dayType, direction);
    const preferred =
      direction === "return"
        ? profile.returnInterchanges?.[0]
        : getUserFavorite(state, cfg, lineId, "outbound");
    const fallbacks =
      direction === "return" ? profile.returnInterchanges : profile.outboundHomeStops;
    const departed = getRecentlyDeparted(
      lineData[lineId]?.[key] || [],
      currentMin,
      30,
      preferred,
      fallbacks
    );
    departed.forEach((t) =>
      recent.push({ lineId, depMin: t._depMin, stop: t._depStop, ago: currentMin - t._depMin })
    );
  }

  const driveMin = Number(
    state.settings?.stationReachMinutes?.CN_FS !== undefined
      ? state.settings.stationReachMinutes.CN_FS
      : (state.settings?.driveCanegrate || 16)
  );
  const canegrateBlock = buildCanegrateBlock(driveMin, currentMin);
  if (canegrateBlock.justMissedCarTrain) {
    const t = canegrateBlock.justMissedCarTrain;
    recent.push({
      lineId: t.line,
      depMin: t.departureMin,
      stop: "canegrate_fs",
      ago: currentMin - t.departureMin,
      customText: `${minsToHHMM(t.departureMin)} (in stazione tra ${t.departureMin - currentMin} min)`
    });
  }

  recent.sort((a, b) => a.ago - b.ago);
  if (!recent.length) return "";
  return `<section class="recent-section">
    <h3>Partiti di recente</h3>
    ${recent
      .slice(0, 5)
      .map(
        (r) => `<div class="recent-item">
      <span>${r.lineId} · ${r.stop === "canegrate_fs" ? "Canegrate FS" : escapeHtml(getStopName(r.stop))}</span>
      <strong>${r.customText ? r.customText : `${r.ago} min fa`}</strong>
    </div>`
      )
      .join("")}
  </section>`;
}

// ─── Train card (hero or standard) ──────────────────────────────────────────

export function renderTrainCard(stationCode, state, currentMin, cfg, isHero = false) {
  const vm = buildTrainCardViewModel(stationCode, state, currentMin, cfg, isHero);
  if (!vm) return "";

  const liveLink = vm.stopId
    ? ` <a href="https://m.trenord.it/store/#/station-details/${vm.stopId}" target="_blank" rel="noopener" class="station-link" title="Orari tempo reale ${escapeHtml(vm.stationName)}">🔴 LIVE</a>`
    : "";

  const timetableBtn = `
    <button type="button" class="btn text-btn view-timetable-btn view-train-timetable-btn" data-station-code="${vm.stationCode}">
      ${CALENDAR_SVG} Orari stazione
    </button>
  `;

  const trainChips = vm.trains
    .slice(0, 4)
    .map(
      (t) => `
    <span class="stop-chip train-chip">
      <small class="train-chip-label" style="color: ${t.color || "var(--accent)"};">${escapeHtml(t.line)}</small>
      <strong class="train-chip-time">${minsToHHMM(t.departureMin)}</strong>
    </span>
  `
    )
    .join("");

  if (vm.isHero) {
    const walkBlockHtml =
      vm.reachMinutes > 0
        ? `
      <div class="time-block walk">
        <div class="time-block-label walk">Uscire di casa</div>
        <h2 class="time-block-value-hero" aria-live="polite" aria-label="Uscire per prendere il treno tra ${vm.wait} minuti">tra ${vm.wait} min</h2>
        <span class="time-block-pill walk">${vm.stationCode === "CN_FS" ? "in auto" : "percorso"}</span>
        <div class="time-block-detail">Partenza ore ${vm.leaveAtHHMM} &middot; ${vm.stationCode === "CN_FS" ? "auto" : "tempo"} ${vm.reachMinutes} min</div>
      </div>
    `
        : `
      <div class="time-block walk time-block--disabled">
        <div class="time-block-label walk">Uscire di casa</div>
        <h2 class="time-block-value-disabled">non imp.</h2>
        <span class="time-block-pill walk time-block-pill--disabled">disattivato</span>
        <div class="time-block-detail time-block-detail--disabled">Imposta i minuti nel tab Treni o Impostazioni</div>
      </div>
    `;

    return `
      <section class="featured-card ${vm.urgency.css} train-hero-card">
        <div class="featured-top">
          <span class="line-pill" style="background: ${vm.firstTrain.color || "var(--accent)"};">${escapeHtml(vm.firstTrain.line)}</span>
          <span class="status-pill ${vm.urgency.css}">${vm.urgency.label}</span>
        </div>
        
        <div class="dep-stop-row dep-stop-row--hero">
          <div class="dep-stop-row-inner">
            <div class="dep-stop-group">
              <span class="dep-stop-label">STAZIONE DI</span>
              <div class="dep-stop-controls">
                <span class="dep-stop-name-text">${escapeHtml(vm.stationName)}</span>
                <button type="button" class="map-trigger dep-stop-pin" data-stop-code="${vm.stationCode}" title="Vedi sulla mappa" aria-label="Apri mappa stazione">${PIN_SVG}</button>
                ${liveLink}
                <span class="dep-stop-dest">${vm.isReturn ? "← Da Milano" : "→ Milano"}</span>
              </div>
            </div>
            ${timetableBtn}
          </div>
        </div>

        <div class="time-info-row time-info-row--no-border time-info-row--margin-top">
          <div class="time-block bus">
            <div class="time-block-label bus">Orario Treno</div>
            <h2 class="time-block-value-hero-time">${vm.depHHMM}</h2>
            <span class="time-block-pill bus" aria-live="polite" aria-label="Treno in partenza tra ${vm.firstTrain.departureMin - currentMin} minuti">tra ${vm.firstTrain.departureMin - currentMin} min</span>
          </div>
          ${walkBlockHtml}
        </div>
        <div class="stop-chip-row train-chip-scroll-row">
          ${trainChips}
        </div>
      </section>
    `;
  } else {
    // Standard Train Card styled EXACTLY like Bus line-card
    const id = `live-train-${vm.stationCode}`;
    const isExpanded = false;
    const nextDisplay = `${vm.depHHMM} · ${formatWait(vm.firstTrain.departureMin - currentMin)}`;
    const routeLabel = vm.isReturn
      ? `${escapeHtml(vm.stationName)} ← Da Milano`
      : `${escapeHtml(vm.stationName)} → Milano`;
    const validityHtml = `<span class="validity-badge">Treno</span>${liveLink}`;

    const urgencyClass =
      vm.urgency.css === "hurry"
        ? "urgency-hurry"
        : vm.urgency.css === "good"
          ? "urgency-good"
          : vm.urgency.css === "calm"
            ? "urgency-calm"
            : "urgency-missed";

    const walkBlock =
      vm.reachMinutes > 0
        ? `
      <div class="time-block walk">
        <div class="time-block-label walk">Uscire di casa</div>
        <div class="time-block-value walk" aria-live="polite" aria-label="Uscire per prendere il treno tra ${vm.wait} minuti">tra ${vm.wait} min</div>
        <span class="time-block-pill walk">${vm.stationCode === "CN_FS" ? "in auto" : "percorso"}</span>
        <div class="time-block-detail">Partenza ore ${vm.leaveAtHHMM} &middot; ${vm.stationCode === "CN_FS" ? "auto" : "tempo"} ${vm.reachMinutes} min</div>
      </div>
    `
        : `
      <div class="time-block walk time-block--disabled">
        <div class="time-block-label walk">Uscire di casa</div>
        <div class="time-block-value walk">non imp.</div>
        <span class="time-block-pill walk time-block-pill--disabled">disattivato</span>
        <div class="time-block-detail time-block-detail--disabled">Imposta i minuti nel tab Treni o Impostazioni</div>
      </div>
    `;

    const depStopHtml = `
      <div class="dep-stop-row dep-stop-row--standard">
        <div class="dep-stop-row-inner">
          <div class="dep-stop-group">
            <span class="dep-stop-label">STAZIONE DI</span>
            <div class="dep-stop-controls">
              <span class="dep-stop-name-text">${escapeHtml(vm.stationName)}</span>
              <button type="button" class="map-trigger dep-stop-pin" data-stop-code="${vm.stationCode}" title="Vedi sulla mappa" aria-label="Apri mappa stazione">${PIN_SVG}</button>
              ${liveLink}
              <span class="dep-stop-dest">${vm.isReturn ? "← Da Milano" : "→ Milano"}</span>
            </div>
          </div>
          ${timetableBtn}
        </div>
      </div>
    `;

    const timeInfoRow = `
      ${depStopHtml}
      <div class="time-info-row">
        <div class="time-block bus">
          <div class="time-block-label bus">Orario Treno</div>
          <div class="time-block-value bus">${vm.depHHMM}</div>
          <span class="time-block-pill bus" aria-live="polite" aria-label="Partenza treno tra ${vm.firstTrain.departureMin - currentMin} minuti">tra ${vm.firstTrain.departureMin - currentMin} min</span>
        </div>
        ${walkBlock}
      </div>
    `;

    const upcomingTrips = `
      <div class="next-list">
        <h4>Prossime partenze</h4>
        <div class="train-upcoming-trips">
          ${trainChips}
        </div>
      </div>
    `;

    return `
      <article class="line-card ${isExpanded ? "expanded" : ""}" data-card="${id}">
        <button class="line-card-header" type="button" data-toggle-card="${id}">
          <div class="line-main">
            <span class="line-pill" style="background: ${vm.firstTrain.color || "var(--accent)"};">${escapeHtml(vm.firstTrain.line)}</span>
            <div>
              <h3>${routeLabel}</h3>
              <p>${validityHtml}</p>
            </div>
          </div>
          <div class="line-meta">
            <strong aria-live="polite" aria-label="Prossimo treno alle ${vm.depHHMM}, tra ${vm.firstTrain.departureMin - currentMin} minuti">${vm.depHHMM} · ${formatWait(vm.firstTrain.departureMin - currentMin)}</strong>
            <span class="urgency-dot ${urgencyClass}"></span>
          </div>
        </button>
        ${timeInfoRow}
        <div class="line-card-body">
          ${upcomingTrips}
        </div>
      </article>
    `;
  }
}

export function getLeaveAtMs(tripDepMin, walkMin, currentMin) {
  const now = new Date();
  let depTime = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    Math.floor(tripDepMin / 60),
    tripDepMin % 60,
    0,
    0
  );
  if (tripDepMin < currentMin - 600) {
    depTime.setDate(depTime.getDate() + 1);
  }
  return depTime.getTime() - walkMin * 60 * 1000;
}
